import DomPurify from "isomorphic-dompurify";
import React from "react";
import Balancer from "react-wrap-balancer";
import { ColumnType, formatNumber, renderCellContent } from "./columns"; // Import the renderCellContent function
import { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "./tableGrid";

type Column = {
  title: string;
  type: ColumnType;
  format?: "plain" | "currency";
  className?: string;
  aggregate?: boolean;
};

enum ColumnValueType {
  TEXT = "text",
  BADGE = "badge",
  LINK = "link",
  STATUS = "status",
  NUMBER = "number"
}

type TableGridProps = {
  title?: string;
  subtitle?: string;
  hideHeaders?: boolean;
  columns?: Column[];
  data?: Record<string, any>[];
  showGroupAggregate?: boolean;
  caption?: string;
  collapsible?: boolean;
  groupBy?: string; // New optional property for grouping
};

type GroupedData = {
  [key: string]: {
    items: Record<string, any>[];
    aggregateValues?: Record<string, number>;
  };
};

// Update the groupDataBy function to use the new GroupedData type
const groupDataBy = (
  data: Record<string, any>[],
  key: string,
  columns?: Column[],
  showGroupAggregate?: boolean
): GroupedData => {
  return data.reduce((groups, item: Record<string, any>) => {
    // Explicitly define the type of 'item'
    const groupValue = item[key];
    if (!groups[groupValue]) {
      groups[groupValue] = { items: [] };
    }
    groups[groupValue].items.push(item);

    // Calculate aggregate values for each group
    if (showGroupAggregate && columns) {
      groups[groupValue].aggregateValues = columns.reduce(
        (acc, column) => {
          if (column.aggregate && column.type === ColumnValueType.NUMBER) {
            acc[column.title] = (groups[groupValue].items || []).reduce((sum: number, item: Record<string, any>) => {
              // Explicitly define the type of 'item'
              const numericValue = parseFloat(item[column.title]);
              return sum + (isNaN(numericValue) ? 0 : numericValue);
            }, 0);
          }
          return acc;
        },
        {} as Record<string, number>
      );
    }

    return groups;
  }, {} as GroupedData);
};

type DataRow = Record<string, any>;

const TableGrid: React.FC<TableGridProps> = ({
  title,
  subtitle,
  columns,
  data,
  caption,
  groupBy,
  collapsible,
  showGroupAggregate,
  hideHeaders
}) => {
  const groupedData = groupBy ? groupDataBy(data || [], groupBy, columns, showGroupAggregate) : null;
  const [collapsedGroups, setCollapsedGroups] = React.useState<Record<string, boolean>>({});
  const toggleGroup = (group: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const aggregateValues =
    columns?.reduce(
      (acc, column) => {
        if (column.aggregate && column.type === ColumnValueType.NUMBER) {
          acc[column.title] = (data || []).reduce((sum, item) => {
            const numericValue = parseFloat(item[column.title]);
            return sum + (isNaN(numericValue) ? 0 : numericValue);
          }, 0);
        }
        return acc;
      },
      {} as Record<string, number>
    ) || {};

  return (
    <section className="tile-container" id="table-grid-80">
      <div className="tile-title-container">
        {title && (
          <p className="tile-main-header">
            <Balancer>{title}</Balancer>
          </p>
        )}
        {subtitle && <p className="tile-subtitle" dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(subtitle) }} />}
      </div>
      <Table className="rounded-xl border">
        {caption && <TableCaption>{caption}</TableCaption>}
        {!hideHeaders && (
          <TableHeader>
            <TableRow>
              {columns?.map((column, index) => (
                <TableHead key={index} className={column.className}>
                  {column.title}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        )}
        <TableBody>
          {groupedData
            ? Object.entries(groupedData).map(([group, groupData], groupIndex) => (
                <React.Fragment key={groupIndex}>
                  <TableRow
                    className={`bg-[#F5F1E4] text-lg ${collapsible ? "cursor-pointer" : ""}`}
                    onClick={() => collapsible && toggleGroup(group)}
                  >
                    <TableCell>{group}</TableCell>
                    {columns?.map((column, index) => (
                      <TableCell key={index}>
                        {showGroupAggregate &&
                        collapsedGroups[group] &&
                        column.aggregate &&
                        column.type === ColumnValueType.NUMBER
                          ? formatNumber(groupData.aggregateValues?.[column.title] || 0, column.format)
                          : ""}
                      </TableCell>
                    ))}
                  </TableRow>
                  {!collapsedGroups[group] &&
                    groupData.items.map((row: DataRow, rowIndex: number) => (
                      <TableRow key={rowIndex}>
                        {columns?.map((column, colIndex) => (
                          <TableCell key={colIndex} className={column.className}>
                            {renderCellContent(column.type, row[column.title], row, column.format)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  {showGroupAggregate && !collapsedGroups[group] && (
                    <TableRow>
                      {columns?.map((column, index) => (
                        <TableCell key={index}>
                          {column.aggregate && column.type === ColumnValueType.NUMBER
                            ? formatNumber(groupData.aggregateValues?.[column.title] || 0, column.format)
                            : ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            : data?.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns?.map((column, colIndex) => (
                    <TableCell key={colIndex} className={column.className}>
                      {renderCellContent(column.type, row[column.title], row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            {columns?.map((column, index) => (
              <TableCell key={index}>
                {column.aggregate && column.type === "number"
                  ? formatNumber(aggregateValues[column.title], column.format)
                  : ""}
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      </Table>
    </section>
  );
};

export default TableGrid;
